/**
 * Stage 4: drafting a pleading against the pleading that came in.
 *
 * Every other document on this screen is assembled from the case record and the
 * officer presses Generate. These four answer a document that arrived in the
 * post — an Original Application, a rejoinder, a writ petition, an order of the
 * Tribunal — so the first thing the screen has to ask is *which paper*, and the
 * first thing it has to report afterwards is *did the draft deal with all of
 * it*.
 *
 * **The coverage strip is the point of the feature.** Rule 12(2) of the CAT
 * (Procedure) Rules requires every averment to be specifically admitted, denied
 * or explained; one that is not stands unrebutted, and s. 22(2) of the Act lets
 * the Tribunal decide on the pleadings. A reply that misses paragraph 14 reads
 * perfectly and loses that paragraph. So the correspondence is counted, shown,
 * and never hidden behind a fold.
 *
 * It reports and does not block. The draft is written either way and the
 * officer decides — the same posture as an ADR-025 mark, for the same reason.
 */

import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { api } from '../api/client'
import type { AnswerGapView, SourcePaperChoice } from '../api/client'

/**
 * The four documents this path serves, and what each one answers.
 *
 * A register id is not a title, so this is not stop list 6: what each document
 * is *called* still comes off the pinned process version and appears in the
 * heading above. What is here is which paper the officer is being asked to
 * point at, which the register cannot tell him.
 */
export const ANSWERS: Record<string, string> = {
  D18: 'the Original Application',
  D19: "the applicant's rejoinder",
  D20: 'the writ petition',
  D21: "the Tribunal's order",
}

/**
 * The three that are answered paragraph by paragraph.
 *
 * **D21 is a pleading and is not one of these.** It is the department's own
 * petition under Article 226 — grounds against the Tribunal's order, not
 * replies to somebody else's paragraphs. It has a source paper, which is why it
 * is in `ANSWERS`, and offering to answer that order paragraph by paragraph
 * would produce a document in the right register with no argument in it. The
 * server refuses it; this is so the officer is told before he presses anything.
 */
export const ANSWERED_PARAWISE = new Set(['D18', 'D19', 'D20'])

/** The label printed before a paragraph number of the paper each one answers. */
export const PAPER_SHORT: Record<string, string> = {
  D18: 'OA',
  D19: 'Rejoinder',
  D20: 'Writ petition',
}

export const paperShort = (documentType: string | undefined): string =>
  PAPER_SHORT[(documentType ?? '').toUpperCase()] ?? 'OA'

export const isAnsweredParawise = (documentType: string | undefined): boolean =>
  Boolean(documentType && ANSWERED_PARAWISE.has(documentType.toUpperCase()))

export const isPleading = (documentType: string | undefined): boolean =>
  Boolean(documentType && documentType.toUpperCase() in ANSWERS)

/** `dd.mm.yyyy` in chrome. The document's own dates are the server's. */
const shortDate = (value: string | null | undefined): string => {
  if (!value) return ''
  const [y, m, d] = value.split('-')
  return y && m && d ? `${d}.${m}.${y}` : value
}

export function paperLabel(paper: SourcePaperChoice, kind = ''): string {
  const bits = [paper.filename]
  // What the office said the file is at upload (Stage 4): Body, Annexures, the
  // office's own file, a filed document. The officer is choosing the Body.
  if (kind) bits.push(kind)
  if (paper.document_type) bits.push(paper.document_type)
  else if (paper.description) bits.push(paper.description)
  if (paper.document_date) bits.push(shortDate(paper.document_date))
  if (paper.page_count) bits.push(`${paper.page_count} pp`)
  // **The rule travels with the option, not after the click.** The paragraph
  // map uploads the paper itself and needs a PDF. A Word file was offered
  // plainly, chosen, drafted against, and only then refused. It stays on the
  // list — a filtered list hides a correctly-filed paper with nothing on
  // screen saying why — and says what is wrong with it.
  if (paper.not_reviewable) bits.push('needs a PDF')
  return bits.join(' · ')
}

export function PleadingDraftPanel({
  caseId,
  documentType,
  instruction,
  onInstruction,
  chosen,
  onChoose,
  model,
  onModel,
  models,
  onDraft,
  pending,
}: {
  caseId: string
  documentType: string
  instruction: string
  onInstruction: (value: string) => void
  chosen: string | null
  onChoose: (id: string | null) => void
  model: string | null
  onModel: (model: string | null) => void
  models: string[]
  onDraft: () => void
  pending: boolean
}) {
  const answers = ANSWERS[documentType.toUpperCase()] ?? 'the incoming pleading'
  const papers = useQuery({
    queryKey: ['pleading-papers', caseId, documentType],
    queryFn: () => api.pleadingSourcePapers(caseId, documentType),
    retry: false,
  })
  const rows = papers.data ?? []

  return (
    <div className="pleading-draft" data-testid="pleading-draft">
      <p className="muted">
        This document answers {answers}. Choose the paper it answers — it is read as it was
        served, page by page, so the reply&apos;s paragraph numbering matches the
        application&apos;s own.
      </p>

      {papers.isLoading ? (
        <p className="small muted">Reading the papers on this file…</p>
      ) : rows.length === 0 ? (
        // Not an error, and not an empty box either. The next act is upload,
        // and the screen says so rather than leaving a disabled button.
        <p className="small muted" data-testid="pleading-no-papers">
          No papers are on this file yet. Upload {answers} through <strong>Papers</strong> on
          the case first — it belongs on the file whatever is drafted from it.
        </p>
      ) : (
        <label className="field">
          <span className="label">Which paper is {answers}?</span>
          <select
            aria-label={`The paper that is ${answers}`}
            value={chosen ?? ''}
            onChange={(e) => onChoose(e.target.value || null)}
            disabled={pending}
            data-testid="pleading-paper"
          >
            <option value="">Choose a paper…</option>
            {rows.map((paper) => (
              <option key={paper.id} value={paper.id} disabled={Boolean(paper.not_reviewable)}>
                {paperLabel(paper)}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="field">
        <span className="label">
          What should this pleading do?{' '}
          <span className="small muted">
            Optional. What the department&apos;s answer is, in your own words.
          </span>
        </span>
        <textarea
          rows={3}
          value={instruction}
          onChange={(e) => onInstruction(e.target.value)}
          disabled={pending}
          placeholder="Deny throughout; the inquiry followed Rule 14 and the officer was heard."
          data-testid="pleading-instruction"
        />
      </label>

      {models.length > 1 ? (
        /* **The choice is recorded on the version it made.** Two drafts of the
           same pleading on different models are only comparable if each says
           which wrote it — and the cost of each is reported beside it, so the
           comparison is quality against money rather than quality alone. */
        <label className="field">
          <span className="label">
            Which model?{' '}
            <span className="small muted">
              The first is this office&apos;s default. A slower one is not always a better
              pleading — read both.
            </span>
          </span>
          <select
            aria-label="Which model drafts this pleading"
            value={model ?? models[0]}
            onChange={(e) => onModel(e.target.value || null)}
            disabled={pending}
            data-testid="pleading-model"
          >
            {models.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <button
        type="button"
        className="btn"
        onClick={onDraft}
        disabled={pending || !chosen}
        data-testid="pleading-generate"
        title={
          chosen
            ? 'Reads the chosen paper and fills this template. The frame — the cause title, the headings, the prayer and the statutory paragraphs — is the published template, not the model.'
            : 'Choose the paper this pleading answers first.'
        }
      >
        {pending ? 'Reading the pleading…' : 'Draft the reply'}
      </button>
      {pending ? (
        // The one place in this product where the whole document is written in
        // the request rather than deferred to a worker. Saying so is better
        // than a spinner that looks stuck: a long pleading is a long read.
        <p className="small muted" style={{ marginTop: 8 }}>
          The whole pleading is read and answered in one pass. A long application takes a
          minute.
        </p>
      ) : null}
    </div>
  )
}

export function AnswerCoverage({
  answered,
  total,
  gaps,
  sourceNumber,
  sourceCourt,
  sourceFiledOn,
  model,
  seconds,
  promptTokens,
  completionTokens,
  cost,
}: {
  answered: number
  total: number
  gaps: AnswerGapView[]
  sourceNumber?: string
  sourceCourt?: string
  sourceFiledOn?: string | null
  model?: string | null
  seconds?: number | null
  promptTokens?: number | null
  completionTokens?: number | null
  cost?: number | null
}) {
  const [open, setOpen] = useState(true)

  // `0 of 0` is shown, not hidden. A reply reported complete against an
  // application whose paragraphs could not be read is the exact false comfort
  // this check exists to remove.
  const complete = total > 0 && answered === total
  const cite = [sourceNumber, sourceCourt, shortDate(sourceFiledOn)].filter(Boolean).join(' · ')

  /* **This one is not a count, it is a warning.** A reply that is the
     application answers nothing however many paragraphs it covers, and the
     draft it appears in reads as the best one yet — 20,391 characters, the
     right cause title, eight correct order numbers. It must not sit in an
     amber list as one row among forty. */
  const copied = gaps.filter((g) => g.kind === 'copied-from-the-application')

  return (
    <div
      className={`answer-coverage${copied.length ? ' lifted' : complete ? ' settled' : ''}`}
      data-testid="answer-coverage"
    >
      {copied.length ? (
        <p className="lifted" data-testid="copied-from-application">
          <strong>
            {copied.length === 1
              ? 'One reply is the application’s own words, not an answer to it.'
              : `${copied.length} replies are the application’s own words, not answers to it.`}
          </strong>{' '}
          Read {copied.length === 1 ? 'it' : 'them'} before anything is signed — a draft that
          repeats the applicant’s case would be filed as the department’s, on oath.
        </p>
      ) : null}

      <div className="row between">
        <p className="strong">
          {total === 0
            ? 'No numbered paragraphs could be read from the pleading this answers.'
            : complete
              ? `Every paragraph answered — ${answered} of ${total}.`
              : `${answered} of ${total} paragraphs answered.`}
        </p>
        {gaps.length ? (
          <button type="button" className="quiet" onClick={() => setOpen((on) => !on)}>
            {open ? 'Hide' : `Show ${gaps.length}`}
          </button>
        ) : null}
      </div>

      {cite ? <p className="small muted mono">{cite}</p> : null}

      {/* **What it cost, beside what it produced.** Two drafts of one pleading
          on two models are comparable only if each says which wrote it and what
          it spent — otherwise the choice is quality against nothing. Tokens are
          counted; money appears only where a rate is configured, because a
          plausible figure beside a real count reads as measured. */}
      {model ? (
        <p className="small muted mono" data-testid="pleading-bill">
          {model}
          {seconds ? ` · ${seconds}s` : ''}
          {promptTokens ? ` · in ${promptTokens.toLocaleString()}` : ''}
          {completionTokens ? ` · out ${completionTokens.toLocaleString()}` : ''}
          {cost != null ? ` · ${cost.toFixed(2)}` : ''}
        </p>
      ) : null}

      {total === 0 ? (
        <p className="small muted">
          Check that the paper you chose is the pleading itself and not its annexures. Nothing
          is lost — the draft stands and this count does not.
        </p>
      ) : !complete ? (
        <p className="small muted">
          Rule 12(2) requires every averment to be admitted, denied or explained. An unanswered
          one stands unrebutted.
        </p>
      ) : null}

      {open && gaps.length ? (
        <ul className="gaps" data-testid="answer-gaps">
          {gaps.map((gap) => (
            <li key={`${gap.kind}-${gap.paragraph}`} className={`gap ${gap.kind}`}>
              <span className="mono">¶{gap.paragraph}</span> <span>{gap.message}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
